/**
 * PartList — one collapsible block per `parts[]` entry in
 * StructuredAssessmentV1. The header shows the part question, the student's
 * answer (objective parts) and the per-part computed scores (Marks + Accuracy +
 * Approach), so a collapsed card still says what was answered and what it
 * earned. The body shows the part's own `stepAnalysis`, the LLM's expected
 * answer for that part, missing_or_wrong prose, and per-part hints; for the
 * admin audience it also shows what the analyser concluded about the part
 * (verdict, answer match, mapping confidence, feedback, worked solution),
 * marked as not shown to the student.
 *
 * Parts that lost marks start open; full-mark parts start collapsed.
 * `not_attempted` parts render header-only and do not collapse.
 *
 * Per-part scores come from `feedback.partResults[]` (keyed by `partId`),
 * computed by the structured scorer's `computePartScores`. Legacy
 * `feedback.partScores` (record keyed by partId) is accepted for backward
 * compatibility with submissions written before the rename. When neither is
 * present, score badges are hidden — the rest of the per-part body still
 * renders.
 */
import type { ComputedScores, QuestionPartResult, StructuredPartAssessment } from '@padcanvas/shared';
import { correctnessFromScore } from '@padcanvas/shared';
import { LatexText } from '../../latex';
import { SCORING_LABELS } from '../../scoring-labels';
import { StepsList } from './StepsList';
import { HintsList } from './HintsList';
import { AdminOnlySection } from './AdminOnlySection';
import type { SolutionAnalysisAudience } from '../audience';

/** Subset of `StructuredPartAssessment` this component renders — the per-part
 *  fields the view reads (`stepAnalysis`, `correctLlmAnswer`,
 *  `solutionGuidance`). */
type RenderablePart = Pick<
    StructuredPartAssessment,
    'partId' | 'partQuestion' | 'attemptStatus' | 'assessment' | 'stepAnalysis' | 'correctLlmAnswer' | 'solutionGuidance' | 'missingOrWrong'
> & Partial<Pick<
    StructuredPartAssessment,
    'partType' | 'partMappingConfidence' | 'feedback' | 'interpretation' | 'selectedOptions' | 'studentAnswer' | 'isCorrect' | 'answerMatch' | 'correctLlmSolution'
>>;

interface Props {
    parts: readonly RenderablePart[] | undefined;
    /** New shape — preferred. Array, ordered by question.parts[]. */
    partResults?: readonly QuestionPartResult[];
    /** @deprecated Legacy shape — record keyed by partId. Read-only fallback
     *  for submissions written before the rename. */
    partScores?: Record<string, ComputedScores>;
    title?: string;
    /** Show each part's `missingOrWrong` (tutor-feedback v2). Defaults to shown;
     *  the student app passes the `analysis_view_v2` gate. */
    showMissingOrWrong?: boolean;
    /** F4 — 3-state / first-break step rendering per part. Same gate. */
    richSteps?: boolean;
    /** The analysis may rest on a misreading — no part anchors a first issue. */
    lowTrust?: boolean;
    /** `'admin'` (default) adds the analyser's per-part conclusions, marked as
     *  not shown to the student. */
    audience?: SolutionAnalysisAudience;
}

interface ResolvedPartScores {
    correctnessScore?: number;
    correctnessScoreComment?: string;
    approachScore?: number;
    marksAwarded?: number;
    maxMarks?: number;
    /** Deterministic attempted flag (Phase 1B: objective parts are attempted
     *  iff the student answered, regardless of the LLM's view). Undefined for
     *  legacy partScores rows. */
    attempted?: boolean;
    /** Phase 1C: false when the question's `requirement` excluded this part
     *  (the un-chosen OR branch / surplus beyond K). Shown as "Not selected". */
    selected?: boolean;
}

function resolvePartScores(
    partId: string,
    partResults: readonly QuestionPartResult[] | undefined,
    partScores: Record<string, ComputedScores> | undefined,
): ResolvedPartScores | undefined {
    const hit = partResults?.find(r => r.partId === partId);
    if (hit) return hit;
    return partScores?.[partId];
}

/** "2/3 marks" badge — shown when the scorer recorded marksAwarded. Falls
 *  back to just the awarded number when maxMarks is absent (legacy rows). */
function MarksBadge({ marksAwarded, maxMarks }: { marksAwarded?: number; maxMarks?: number }) {
    if (marksAwarded == null) return null;
    const text = maxMarks != null ? `${marksAwarded}/${maxMarks}` : `${marksAwarded}`;
    return (
        <span className="sol-part-score-badge sol-part-marks-badge">
            <span className="sol-part-score-label">Marks</span>
            <span className="sol-part-score-value">{text}</span>
        </span>
    );
}

function ScoreBadge({ label, value }: { label: string; value: number | undefined }) {
    if (value == null) return null;
    return (
        <span className="sol-part-score-badge">
            <span className="sol-part-score-label">{label}</span>
            <span className="sol-part-score-value">{value}</span>
        </span>
    );
}

/** The student's answer as the analyser read it: the option(s) for a choice
 *  part, else the written value. Undefined when nothing was read. */
function studentAnswerText(part: RenderablePart): string | undefined {
    const options = part.selectedOptions?.filter(o => o.trim());
    if (options?.length) return options.join(', ');
    return part.studentAnswer?.trim() || undefined;
}

/** A part that earned everything it could — starts collapsed. */
function earnedFullMarks(scores: ResolvedPartScores | undefined): boolean {
    if (scores?.marksAwarded != null && scores.maxMarks != null) return scores.marksAwarded >= scores.maxMarks;
    return scores?.correctnessScore === 10;
}

/** The analyser's own conclusions about this part — what the score was built
 *  from, and what to compare it against when the two disagree. */
function AnalyserReading({ part }: { part: RenderablePart }) {
    const rows: Array<[string, string]> = [];
    const verdict = [part.assessment, part.isCorrect === undefined ? '' : `is_correct: ${part.isCorrect}`].filter(Boolean).join(' · ');
    if (verdict) rows.push(['Analyser verdict', verdict]);
    if (part.answerMatch) {
        const { equivalent, normalizedStudent, normalizedCorrect } = part.answerMatch;
        rows.push(['Answer match', `${equivalent} — student ${normalizedStudent || '∅'}, key ${normalizedCorrect || '∅'}`]);
    }
    if (part.partMappingConfidence != null) rows.push(['Part mapping confidence', String(part.partMappingConfidence)]);
    const feedback = part.feedback?.trim();
    const approachSummary = part.interpretation?.approachSummary?.trim();
    const solution = part.correctLlmSolution?.trim();
    if (rows.length === 0 && !feedback && !approachSummary && !solution) return null;
    return (
        <AdminOnlySection>
            {rows.length > 0 && (
                <dl className="sol-part-fields">
                    {rows.map(([k, v]) => (
                        <div key={k} className="sol-part-field">
                            <dt>{k}</dt>
                            <dd><LatexText text={v} /></dd>
                        </div>
                    ))}
                </dl>
            )}
            {feedback && (
                <section className="sol-section">
                    <h4 className="sol-section-title">Analyser feedback</h4>
                    <p><LatexText text={feedback} /></p>
                </section>
            )}
            {approachSummary && (
                <section className="sol-section">
                    <h4 className="sol-section-title">Approach summary</h4>
                    <p><LatexText text={approachSummary} /></p>
                </section>
            )}
            {solution && (
                <section className="sol-section">
                    <h4 className="sol-section-title">Worked solution</h4>
                    <p className="sol-part-solution"><LatexText text={solution} /></p>
                </section>
            )}
        </AdminOnlySection>
    );
}

function PartCard({ part, index, scores, showMissingOrWrong, richSteps, lowTrust, audience }: { part: RenderablePart; index: number; scores: ResolvedPartScores | undefined; showMissingOrWrong: boolean; richSteps: boolean; lowTrust: boolean; audience: SolutionAnalysisAudience }) {
    const partKey = part.partId || `part_${index + 1}`;
    const missingOrWrong = showMissingOrWrong ? part.missingOrWrong : undefined;
    // A part collapses to header-only ONLY when it has nothing to show. When the
    // LLM produced per-part analysis (steps / expected answer / guidance / a
    // score note), render the body even if a (possibly stale) `attempted` flag
    // says false — otherwise the same data renders differently across the Model
    // Run and Solution Analysis views (one hides the steps the other shows).
    const answer = studentAnswerText(part);
    // A free-form answer is prose — it goes in the body, not the header.
    const answerInHeader = !!answer && part.partType !== undefined && part.partType !== 'FREEFORM';
    const hasBody = (part.stepAnalysis?.length ?? 0) > 0
        || !!part.correctLlmAnswer
        || !!answer
        || (part.solutionGuidance?.length ?? 0) > 0
        || !!missingOrWrong
        || !!scores?.correctnessScoreComment;
    // Phase 1C: a part the requirement excluded (the un-chosen OR branch, or
    // surplus beyond K) — the student chose another option. Header-only,
    // labeled "Not selected", never showing a 0/N marks badge that would read
    // like a wrong answer.
    const isNotSelected = scores?.selected === false;
    // The deterministic attempted flag (partResults) wins over the LLM's
    // attemptStatus — for objective parts the student's explicit answer is
    // the ground truth (Phase 1B).
    const markedUnattempted = scores?.attempted === false
        || (scores?.attempted === undefined && part.attemptStatus === 'not_attempted');
    const isSkipped = isNotSelected || (markedUnattempted && !hasBody);
    const variant = isNotSelected ? 'not_selected' : isSkipped ? 'not_attempted' : part.attemptStatus;
    const header = (
        <>
            <div className="sol-part-id">{partKey}</div>
            <div className="sol-part-question"><LatexText text={part.partQuestion} /></div>
            <div className="sol-part-scores">
                {answerInHeader && (
                    <span className="sol-part-score-badge sol-part-answer-badge">
                        <span className="sol-part-score-label">{audience === 'student' ? 'Your answer' : 'Answer'}</span>
                        <span className="sol-part-score-value"><LatexText text={answer!} /></span>
                    </span>
                )}
                {isNotSelected ? (
                    <span className="sol-part-score-badge sol-part-not-selected-badge">Not selected</span>
                ) : (
                    <>
                        <MarksBadge marksAwarded={scores?.marksAwarded} maxMarks={scores?.maxMarks} />
                        <ScoreBadge label={SCORING_LABELS.correctness} value={scores?.correctnessScore} />
                        <ScoreBadge label={SCORING_LABELS.approach} value={scores?.approachScore} />
                    </>
                )}
            </div>
        </>
    );
    const className = `sol-part sol-part--${variant} sol-part--assessment-${part.assessment}`;
    if (isSkipped) {
        return (
            <article className={className}>
                <header className="sol-part-header">{header}</header>
            </article>
        );
    }
    return (
        <details className={`${className} sol-part--collapsible`} open={!earnedFullMarks(scores)}>
            <summary className="sol-part-header">{header}</summary>
            <div className="sol-part-body">
                {/* Deterministic scorer note for objective parts —
                    e.g. "Incorrect option selected", "Close but not exact value". */}
                {scores?.correctnessScoreComment && (
                    <p className="sol-part-score-comment">{scores.correctnessScoreComment}</p>
                )}
                {answer && !answerInHeader && (
                    <section className="sol-section sol-part-student-answer">
                        <h4 className="sol-section-title">{audience === 'student' ? 'Your answer' : "Student's answer"}</h4>
                        <p><LatexText text={answer} /></p>
                    </section>
                )}
                {/* Per-part "why" — what's missing or wrong in this part
                    (FREEFORM parts). Tutor-feedback v2; gated by
                    `showMissingOrWrong`. */}
                {missingOrWrong && (
                    <p className="sol-part-missing"><LatexText text={missingOrWrong} /></p>
                )}
                <StepsList steps={part.stepAnalysis ?? []} title="" richSteps={richSteps} lowTrust={lowTrust} />
                {part.correctLlmAnswer && (
                    <section className="sol-section sol-part-correct-answer">
                        <h4 className="sol-section-title">Expected answer for this part</h4>
                        <p><LatexText text={part.correctLlmAnswer} /></p>
                    </section>
                )}
                <HintsList items={part.solutionGuidance} title="Guidance for this part" />
                {audience === 'admin' && <AnalyserReading part={part} />}
            </div>
        </details>
    );
}

export function PartList({ parts, partResults, partScores, title = 'Part-by-Part Analysis', showMissingOrWrong = true, richSteps = false, lowTrust = false, audience = 'admin' }: Props) {
    // Objective parts score deterministically and may be absent from the
    // LLM's parts[] — synthesize a header-only card from the partResult so
    // every scored part is visible (Phase 1B).
    const llmPartIds = new Set((parts ?? []).map(p => p.partId));
    const resultOnlyParts: RenderablePart[] = (partResults ?? [])
        .filter(r => !llmPartIds.has(r.partId))
        .map(r => {
            // Correctness from the part's score (canonical >=7 / >=4), not the
            // old ad-hoc ===10 / >0 bands. 'unknown' (no score) on an attempted
            // part reads as incorrect, matching the prior behavior.
            const band = correctnessFromScore(r.correctnessScore);
            return {
                partId: r.partId,
                partQuestion: '',
                attemptStatus: r.attempted ? 'attempted' : 'not_attempted',
                assessment: !r.attempted ? 'cannot_determine'
                    : band === 'unknown' ? 'incorrect' : band,
                stepAnalysis: undefined,
                correctLlmAnswer: '',
                solutionGuidance: [],
                missingOrWrong: undefined,
            };
        });
    const allParts = [...(parts ?? []), ...resultOnlyParts];
    if (allParts.length === 0) return null;
    return (
        <section className="sol-section sol-multipart">
            <h3 className="sol-section-title">{title}</h3>
            <div className="sol-parts-list">
                {allParts.map((p, i) => {
                    const key = p.partId || `part_${i}`;
                    return (
                        <PartCard
                            key={key}
                            part={p}
                            index={i}
                            scores={resolvePartScores(key, partResults, partScores)}
                            showMissingOrWrong={showMissingOrWrong}
                            richSteps={richSteps}
                            lowTrust={lowTrust}
                            audience={audience}
                        />
                    );
                })}
            </div>
        </section>
    );
}
